/**
 * IdleAttractorScope + BreathingAttractor — the Single Attractor Law in React.
 *
 * One IdleAttractorScope per screen/route. Inside it, exactly ONE
 * BreathingAttractor may be live. Dev: a second registration THROWS naming
 * both attractors. Prod: second registration is ignored + console.warn.
 */

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { AttractorRegistry } from "./registry.js";
import { applyPrimitive } from "../primitives/apply.js";
import { useJuiceService } from "../context.jsx";

const ScopeContext = createContext<AttractorRegistry | null>(null);

export interface IdleAttractorScopeProps {
  /** Names the scope in violation messages (e.g. the route). */
  name?: string;
  /** Test/dev override for environment detection. */
  isDev?: boolean;
  children?: ReactNode;
}

export function IdleAttractorScope({ name, isDev, children }: IdleAttractorScopeProps) {
  const registry = useMemo(
    () =>
      new AttractorRegistry(
        isDev === undefined ? { scopeName: name ?? "screen" } : { scopeName: name ?? "screen", isDev },
      ),
    [name, isDev],
  );
  return <ScopeContext.Provider value={registry}>{children}</ScopeContext.Provider>;
}

/** The scope's registry — throws in dev when used outside a scope. */
export function useAttractorScope(): AttractorRegistry | null {
  return useContext(ScopeContext);
}

/**
 * Claims the scope's single attractor slot for this component's lifetime.
 * Returns true when this instance holds the slot. Outside any scope the claim
 * always succeeds (an unscoped screen is the caller's own responsibility —
 * every screen SHOULD mount a scope).
 */
export function useIdleAttractor(attractorName: string): boolean {
  const registry = useContext(ScopeContext);
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!registry) {
      setLive(true);
      return () => setLive(false);
    }
    // Dev double-registration throws here — intentionally loud.
    const registration = registry.register(attractorName);
    setLive(registration.live);
    return () => {
      registration.release();
      setLive(false);
    };
  }, [registry, attractorName]);

  return live;
}

export interface BreathingAttractorProps {
  /** Names this attractor in violation messages. */
  name: string;
  /** Pause the breathe loop (element keeps its slot). */
  paused?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

/**
 * BREATHE — scale 1↔1.03 @ 1800ms — the ONE "look here" element per screen.
 * Registers with the enclosing IdleAttractorScope; respects reduced motion
 * (no loop) and pauses cleanly.
 */
export function BreathingAttractor({ name, paused, className, style, children }: BreathingAttractorProps) {
  const live = useIdleAttractor(name);
  const service = useJuiceService();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !live || paused) return;
    const anim = applyPrimitive(el, "breathe", { service });
    return () => anim?.cancel();
  }, [live, paused, service]);

  return (
    <div ref={ref} className={className} style={style} data-juice-attractor={name}>
      {children}
    </div>
  );
}
